import type {
  PlayerAIChatMessage,
  PlayerAIToolProposalPayload,
} from "./playerai.types";
import { proposalCanvasDeliveryState } from "./proposal-status";
import { proposalTitle } from "./proposal-presentation";

/**
 * Task 13 — which proposal sections may fold into a compact row.
 *
 * A section NEEDS ATTENTION, and therefore never folds, while its decision or
 * its effect is still open: awaiting approval, applying, a review form this
 * bundle cannot render, or an applied change whose canvas write has not been
 * confirmed (it can still be retried or discarded). Everything else —
 * applied, rejected, expired, stale, failed — is COMPLETED.
 */
export function proposalNeedsAttention(
  payload: PlayerAIToolProposalPayload,
): boolean {
  if (payload.status === "pending" || payload.status === "applying") {
    return true;
  }
  if (payload.unsupportedReviewForm === true) return true;
  return proposalCanvasDeliveryState(payload) !== "confirmed";
}

/**
 * The compact row's description, in each proposal type's own words: a canvas
 * bundle counts its changes; every other type keeps its own title. Never
 * "canvas changes" for something that is not one.
 */
export function proposalCollapsedSummary(
  payload: PlayerAIToolProposalPayload,
): string {
  const preview = payload.preview;
  if (preview?.kind === "canvas_bundle" && Array.isArray(preview.changes)) {
    const count = preview.changes.length;
    return count === 1 ? "1 canvas change" : `${count} canvas changes`;
  }
  return proposalTitle(payload);
}

/**
 * The newest proposal section in the thread. "Latest" is the newest section
 * even when its own Player AI response, or later ordinary messages, sit below
 * it — and newest by when it was CREATED, because a late proposal is placed
 * above the older response it belongs to (Task 5), where position alone would
 * name the wrong one. Rows without a timestamp fall back to position.
 */
export function latestProposalRowId(
  rows: readonly PlayerAIChatMessage[],
): string | null {
  let latest: { readonly id: string; readonly at: number } | null = null;
  for (const row of rows) {
    if (row.widget?.kind !== "toolProposal") continue;
    const parsed = row.ts ? Date.parse(row.ts) : Number.NaN;
    const at: number = Number.isFinite(parsed)
      ? parsed
      : (latest?.at ?? Number.NEGATIVE_INFINITY);
    // `>=`: of two equally new sections, the later one in the thread wins.
    if (latest === null || at >= latest.at) latest = { id: row.id, at };
  }
  return latest?.id ?? null;
}
